// ts-rest contract for Comply > Overdrawn Cash Escalations (feedback Page 2).
//
// The Compliance-side queue of overdrawn-cash rows a desk IM (or Ops) escalated.
// Reads `OverdraftResolution` rows with `escalationLevel > 0` joined to their
// `OdCashRow` (by snapshotId + rowIndex) for client/account/balance display.
// The controller is the mapping seam — it composes the side-table escalation
// fields with the immutable OdCashRow source row.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const overdrawnEscalationRowSchema = z.object({
    // Stable per (snapshotId, rowIndex) — the OverdraftResolution side-row id.
    id: z.string(),
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    // Display fields joined from the source OdCashRow.
    clientReference: z.string(),
    investmentManager: z.string(),
    accountReference: z.string(),
    cashAccountName: z.string(),
    currency: z.string(),
    postedBalance: z.number(),
    // Escalation state from the side table.
    escalationLevel: z.number().int().nonnegative(),
    escalationReason: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    // Whether the row has already been resolved (escalation is independent of
    // resolution — Compliance sees both states).
    resolutionNote: z.string().nullable(),
});
export type OverdrawnEscalationRowDto = z.infer<typeof overdrawnEscalationRowSchema>;

export const overdrawnEscalationsResponseSchema = z.object({
    rows: z.array(overdrawnEscalationRowSchema),
});
export type OverdrawnEscalationsResponse = z.infer<typeof overdrawnEscalationsResponseSchema>;

const errorBody = z.object({ error: z.string() });

export const complyOverdrawnEscalationsContract = c.router(
    {
        /**
         * GET /api/comply/overdrawn-escalations
         *
         * Every escalated overdrawn-cash row (escalationLevel > 0), newest first.
         * Empty `rows` when nothing has been escalated — not a 404.
         */
        list: {
            method: 'GET',
            path: '/comply/overdrawn-escalations',
            responses: {
                200: overdrawnEscalationsResponseSchema,
                403: errorBody,
            },
            summary: 'List overdrawn-cash rows escalated to Compliance (feedback Page 2).',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
